import { useEffect, useRef, useState } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { cn } from '@/lib/utils';
import { Alert } from '@/components/ui/alert';
import { useIsAdminViewer } from '../hooks/use-is-admin-viewer';
import {
  ingestFile,
  listMissingProgrammes,
  saveMissingProgrammeNote,
  type MissingProgrammeRow,
  type NatureSummary,
} from '../lib/missing-programmes';

const cardClass = "bg-card border border-border rounded-[16px] p-[14px]";
const boldClass = "font-extrabold";
const inputClass = "w-full py-[7px] px-[9px] border border-border rounded-[8px] font-[inherit] text-[13px] bg-secondary";

export default function AdminMissingProgrammes() {
  const isAdminViewer = useIsAdminViewer();
  const [items, setItems] = useState<MissingProgrammeRow[] | null>(null);
  const [natureSummary, setNatureSummary] = useState<NatureSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = () =>
    listMissingProgrammes()
      .then((r) => {
        setItems(r.items);
        setNatureSummary(r.natureSummary);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));

  useEffect(() => {
    reload();
  }, []);

  return (
    <>
      <HeaderContent width="xl" showBurger>
        <h1 className={HEADER_TITLE_CLASS}>Programmes manquants</h1>
      </HeaderContent>
      <main className="max-w-[1240px] mx-auto px-4 pb-16 pt-4 flex flex-col gap-4">
        {error && <Alert>{error}</Alert>}

        {natureSummary && <NatureSummaryCard summary={natureSummary} />}

        <div className={cardClass}>
          <div className={cn(boldClass, "mb-2")}>
            Textes détectés par la veille, absents du corpus {items ? `(${items.length})` : ''}
          </div>
          <div className="text-[13px] text-muted-foreground mb-3">
            Source : la veille du Bulletin officiel (<code>veille_runs</code>). Un texte disparaît de
            cette liste dès qu'il est ingéré avec succès — jamais sur la base de son titre.
          </div>

          {!items ? (
            <div className="text-muted-foreground">Chargement…</div>
          ) : items.length === 0 ? (
            <div className="text-muted-foreground">Aucun texte manquant connu.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="pb-2 pr-3">Texte officiel</th>
                    <th className="pb-2 pr-3">Nature</th>
                    <th className="pb-2 pr-3">Détecté le</th>
                    <th className="pb-2 pr-3">Raison de l'échec automatique</th>
                    <th className="pb-2 pr-3">Entrée en vigueur</th>
                    <th className="pb-2 pr-3">Ce que l'absence empêche</th>
                    <th className="pb-2 pr-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <MissingProgrammeRowView
                      key={row.url}
                      row={row}
                      readOnly={!isAdminViewer}
                      onSaved={reload}
                      onError={(e) => setError(e)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function NatureSummaryCard({ summary }: { summary: NatureSummary }) {
  return (
    <div className={cardClass}>
      <div className={cn(boldClass, "mb-2")}>Nature des documents déjà ingérés</div>
      <div className="flex gap-4 flex-wrap text-[13px]">
        <Stat label="Programme" value={summary.programme} />
        <Stat label="Accompagnement" value={summary.accompagnement} />
        <Stat label="Nature à déterminer" value={summary.indetermine} highlight={summary.indetermine > 0} />
        <Stat label="Total" value={summary.total} />
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className={cn("border border-border rounded-[10px] py-1.5 px-3", highlight && "bg-naia-info-bg")}>
      <span className="font-extrabold">{value}</span>
      <span className="text-muted-foreground ml-1.5">{label}</span>
    </div>
  );
}

function decodeFilename(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function MissingProgrammeRowView({
  row,
  readOnly,
  onSaved,
  onError,
}: {
  row: MissingProgrammeRow;
  readOnly: boolean;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [entreeEnVigueur, setEntreeEnVigueur] = useState(row.note.entree_en_vigueur ?? '');
  const [impactEnseignant, setImpactEnseignant] = useState(row.note.impact_enseignant ?? '');
  const [savingNote, setSavingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const rawFilename = row.url.split('/').pop() || row.url;
  const filename = decodeFilename(rawFilename);

  const saveNote = async (patch: { entreeEnVigueur: string; impactEnseignant: string }) => {
    setSavingNote(true);
    try {
      await saveMissingProgrammeNote(row.url, {
        entreeEnVigueur: patch.entreeEnVigueur.trim() || null,
        impactEnseignant: patch.impactEnseignant.trim() || null,
      });
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingNote(false);
    }
  };

  const onFileChosen = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      await ingestFile(file, row.url);
      onSaved();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <tr className="border-t border-border align-top">
      <td className="py-2 pr-3 max-w-[220px]">
        <a href={row.url} target="_blank" rel="noreferrer" className="text-naia-blue font-bold break-all">
          {filename}
        </a>
        {row.status === 'echec_ocr' && (
          <div className="mt-1">
            <span className="border border-border rounded-[6px] py-[1px] px-[6px] font-extrabold text-[11px] bg-naia-info-bg">
              échec OCR
            </span>
          </div>
        )}
      </td>
      <td className="py-2 pr-3 whitespace-nowrap">{row.nature ?? '—'}</td>
      <td className="py-2 pr-3 whitespace-nowrap">{new Date(row.first_detected_at).toLocaleDateString('fr-FR')}</td>
      <td className="py-2 pr-3 max-w-[260px] text-muted-foreground">{row.raison}</td>
      <td className="py-2 pr-3 min-w-[150px]">
        <input
          type="date"
          value={entreeEnVigueur}
          onChange={(e) => setEntreeEnVigueur(e.target.value)}
          onBlur={() => saveNote({ entreeEnVigueur, impactEnseignant })}
          disabled={savingNote || readOnly}
          className={inputClass}
        />
      </td>
      <td className="py-2 pr-3 min-w-[220px]">
        <textarea
          value={impactEnseignant}
          onChange={(e) => setImpactEnseignant(e.target.value)}
          onBlur={() => saveNote({ entreeEnVigueur, impactEnseignant })}
          disabled={savingNote || readOnly}
          placeholder="ex : pas de programme pour évaluer ce cycle en arts plastiques"
          rows={2}
          className={cn(inputClass, "resize-y")}
        />
      </td>
      <td className="py-2 pr-3 whitespace-nowrap">
        {!readOnly && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => onFileChosen(e.target.files?.[0])}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "py-[7px] px-3 bg-naia-blue text-card border-none rounded-sm font-extrabold cursor-pointer font-[inherit] text-[12px] shadow-card",
                uploading && "bg-naia-info-bg text-muted-foreground cursor-wait shadow-none",
              )}
            >
              {uploading ? 'Import…' : 'Déposer un fichier'}
            </button>
          </>
        )}
      </td>
    </tr>
  );
}
